import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'hooks/useInjectReducer';
import { useInjectSaga } from 'hooks/useInjectSaga';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FORMAT, PRESETS } from 'components/atoms/DatePicker';

import { makeSelectIsLoading } from 'redux/loadingSelector';
import { useLazyDownloadLikelyAppliedQuery } from 'services/organizationsV2';
import { activeNetworkSelector } from 'redux/selectors';
import { toast } from 'react-hot-toast';
import { useLazyGetNetworkCountsQuery } from '../../../../services/search';
import {
  loadAllJobAlertSubscribers,
  loadJobAlertSubscribers,
  loadJobsApplicants,
  loadJobsClicks,
  loadJobsLikelyApplied,
  loadSuggestedCompaniesCount,
  loadUniqueVisitors,
} from './actions';
import { analyticSelector, key } from './selector';
import saga from './saga';
import reducer from './reducer';

export const useAnalytics = () => {
  useInjectSaga({ key, saga });
  useInjectReducer({ key, reducer });

  const [loadNetworkCounts, { isLoading: networkCountsLoading, data: networkCounts, isError: networkCountsError }] =
    useLazyGetNetworkCountsQuery();
  const dispatch = useDispatch();
  const analyticData = useSelector(analyticSelector);
  const [dates, setStartDate] = useState({
    startDate: PRESETS[2].start,
    endDate: PRESETS[2].end,
  });
  const initialLoadRef = useRef(null);
  const [download, { isFetching }] = useLazyDownloadLikelyAppliedQuery();
  const currentNetwork = useSelector(activeNetworkSelector);

  const startDate = useMemo(() => dates.startDate.format(FORMAT), [dates.startDate]);
  const endDate = useMemo(() => dates.endDate.format(FORMAT), [dates.endDate]);
  const loadingState = {
    uniqueVisitorsLoading: useSelector(makeSelectIsLoading('uniqueVisitors')),
    jobClicksLoading: useSelector(makeSelectIsLoading('jobsClicks')),
    jobAlertLoading: useSelector(makeSelectIsLoading('allJobAlertSubscribers')),
    allJobAlertLoading: useSelector(makeSelectIsLoading('suggestedCompaniesCount')),
    companiesLoading: useSelector(makeSelectIsLoading('jobAlertSubscribers')),
    jobsApplicantsLoading: useSelector(makeSelectIsLoading('jobsApplicants')),
    downloadLikelyAppliedLoading: isFetching,
    jobsLikelyAppliedLoading: useSelector(makeSelectIsLoading('jobsLikelyApplied')),
    networkCountsLoading,
  };

  useEffect(() => {
    dispatch(loadUniqueVisitors({ startDate, endDate }));
    dispatch(loadJobsClicks({ startDate, endDate }));
    dispatch(loadJobAlertSubscribers({ startDate, endDate }));
    dispatch(loadJobsApplicants({ startDate, endDate }));
    dispatch(loadJobsLikelyApplied({ startDate, endDate }));

    if (!initialLoadRef.current) {
      dispatch(loadSuggestedCompaniesCount());
      dispatch(loadAllJobAlertSubscribers());
      loadNetworkCounts();
    }

    initialLoadRef.current = true;
  }, [dispatch, startDate, endDate, loadNetworkCounts]);

  const onDownloadLikelyApplied = useCallback(async () => {
    await download({ networkId: currentNetwork.id, startDate, endDate });
    toast('Export in progress — we’ll email it to you in a few minutes.', {
      icon: 'success',
      id: 'download_job_application',
    });
  }, [currentNetwork.id, download, endDate, startDate]);

  return {
    ...analyticData,
    networkCounts: {
      ...networkCounts,
      error: networkCountsError,
    },
    dates,
    setStartDate,
    onDownloadLikelyApplied,
    loadingState,
  };
};
